import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAppDispatch } from 'hooks';
import { Label } from 'reactstrap';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { useFormikContext } from 'formik';

import { allocatedOfSections } from '~support-at-home/lib/budgetFigures';
import { deriveBaseAvailableAmount, showCaseManagement } from '~support-at-home/lib/derive';

import { Button, ClickableIcon, ConfirmDialog, ErrorResponse, Toolbar, TooltipPortal } from '~components';
import { Switch } from '~components/form-fields/inputs';
import { InputProps } from '~components/form-fields/props';
import { faRefresh } from '~components/icon';

import { isSupplementRateStale } from '~lookups/models';
import type { Supplement } from '~lookups/models';
import type { Budget, Section, SectionItem } from '~support-at-home/models';

import { getAll as getServices } from '~appointments/actions/serviceTypes';
import { getAll as getCalendarFrequencies } from '~lookups/actions/calendarFrequencies';
import { getAll as getSahCategories } from '~lookups/actions/sahCategories';
import { getAll as getShifts } from '~lookups/actions/shifts';
import {
  buildCopySections,
  buildSections,
  useGetContributionsQuery,
  useGetFilteredServicesQuery,
  useRecalculateMutation,
} from '~support-at-home/actions/budgets';

import { refreshItemDatesToServicePeriods } from '../../forms/budgets/servicePeriods';
import BudgetDetailsModal from './BudgetDetailsModal';
import BudgetGrandTotal from './BudgetGrandTotal';
import BudgetSection from './BudgetSection';
import BudgetSupplementsModal from './BudgetSupplementsModal';
import BudgetSupplementTotal from './BudgetSupplementTotal';
import CategoryMoveNotice, { groupMovesFromSections } from './CategoryMoveNotice';
import BudgetTotalIndicator from './BudgetTotalIndicator';

interface ValueProps extends Partial<Budget> {
  price_plan_deleted: boolean;
  source_budget_id?: string;
}

interface ComponentProps extends InputProps {
  value: ValueProps;
  onChange?: (name: string, value: any) => void;
}

const BudgetSections: React.FC<ComponentProps> = ({ name, value, onChange }) => {
  const dispatch = useAppDispatch();

  // retrieve initial budget from formik context - only needed for edit flows
  const { values } = useFormikContext<{ initial_budget?: Budget }>();
  const initialBudget = values?.initial_budget ?? null;

  const recalcKey = useRef<string | null>(null);
  const isRecalcRunning = useRef(false);
  const pendingSectionsRef = useRef<Section[] | null>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The cost-relevant fingerprint of each item as at the last recalc, so a change can be attributed
  // to the rows that actually caused it rather than to the whole budget.
  const recalcItemKeys = useRef<Map<string, string>>(new Map());
  // The sections as they stand RIGHT NOW, which is not the same thing as the snapshot a recalc was
  // dispatched with. A recalc merges its figures into this, never into its own snapshot - see
  // runRecalc.
  const latestSections = useRef<Section[]>([]);

  // Rows whose figures are known to be out of date: changed since the last recalc and not yet
  // recalculated. Drives the spinner on their price and total, and on the totals that include them.
  const [pendingItemIds, setPendingItemIds] = useState<Set<string>>(new Set());

  const [isSupplementsOpen, setIsSupplementsOpen] = useState<boolean>(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState<boolean>(false);
  const [pendingDelete, setPendingDelete] = useState<{ id: string; category_id: string } | null>(null);
  const [copyError, setCopyError] = useState<string | null>(null);
  // Set when a recalculation failed, so the rows it never reached can say why they are still
  // waiting rather than spinning with no explanation. Cleared by the next run that succeeds.
  const [recalcError, setRecalcError] = useState<string | null>(null);
  // Carry services whose contribution category changes over the copied dates into the category that
  // applies. On by default: the funding a budget allocated to a service is preserved unless a
  // provider decides otherwise, which is the whole point of the change.
  const [applyCategoryMoves, setApplyCategoryMoves] = useState(true);
  // True while a reconcile is in flight, so the switch can say so and refuse to be pressed again.
  const [isReloadingCopy, setIsReloadingCopy] = useState(false);
  // Which reconcile is the current one. Every run takes a ticket and only the holder of the latest is
  // allowed to write its result into the form: these requests are fired from a control a provider can
  // press repeatedly, they are not cancellable, and they do not necessarily come back in the order
  // they were sent - so without this the budget on screen is whichever response happened to land last
  // rather than the one matching the switch. Disabling the control makes that unlikely; this makes it
  // impossible, and the two are not the same thing where the wrong answer is a wrong contribution rate.
  const copyRequestId = useRef(0);

  const {
    id,
    price_plan_revision_id,
    client_id,
    client_sah_budget_id,
    package_fixed_id,
    start_date,
    end_date,
    sections = [],
    total_amount = 0,
    total_allocated = 0,
    total_includes_case_management,
    ignore_sah_filters = false,
    grand_total = { cost: 0, contribution: 0, govSubsidy: 0, case_management: 0 },
    price_plan_deleted = false,
    supplements = [],
    available_amount = 0,
    source_budget_id,
  } = value || {};

  const isNew = id === undefined;

  // Computed client-side (not from the server flag) so it updates LIVE as the modal restages
  const supplementsStale = ((supplements as Supplement[]) || []).some((sup) => isSupplementRateStale(sup.supplement));

  // INITIALIZE available_amount from derived base amount on mount (before recalc runs)
  useEffect(() => {
    if (available_amount === 0 && total_amount > 0) {
      const derivedAmount = deriveBaseAvailableAmount({
        package_fixed_id,
        total_amount,
        total_includes_case_management,
      });
      if (derivedAmount > 0) {
        onChange?.('available_amount', derivedAmount);
      }
    }
  }, []);

  /**
   * Reconcile the source budget against the copy's dates and put the result into the form.
   *
   * Takes the choice as an argument rather than reading it off state: the toggle calls this in the
   * same tick it sets the state, and a stale read would reconcile against the previous answer.
   *
   * Re-running this REPLACES the sections, so any editing already done on this step is lost. That is
   * the honest behaviour for the toggle - it changes which sections exist and what they are rated at,
   * and the server is the only thing that knows the destination category's contribution rate - and it
   * is why the control sits at the top of the step, before there is anything to lose.
   */
  const loadCopySections = useCallback(
    (applyMoves: boolean) => {
      if (!source_budget_id || !client_id || !price_plan_revision_id || !package_fixed_id) return;

      copyRequestId.current += 1;
      const requestId = copyRequestId.current;
      setIsReloadingCopy(true);

      dispatch(
        buildCopySections({
          id: source_budget_id,
          revision_id: price_plan_revision_id,
          client_id: typeof client_id === 'string' ? parseInt(client_id, 10) : client_id,
          package_fixed_id: typeof package_fixed_id === 'string' ? parseInt(package_fixed_id, 10) : package_fixed_id,
          start_date: start_date ?? '',
          end_date: end_date ?? '',
          ignore_sah_filters,
          apply_category_moves: applyMoves,
        }),
      )
        .then((res: { sections: Section[]; supplements: Supplement[] }) => {
          // Superseded: a later toggle has already been sent, and its answer is the one that matches
          // what the switch now says. Writing this one would put the budget out of step with the
          // control that asked for it.
          if (requestId !== copyRequestId.current) return;

          if (res?.sections?.length > 0) {
            onChange?.(name, res.sections);
            if (res.supplements?.length > 0) {
              onChange?.('supplements', res.supplements);
            }
          }
          setCopyError(null);
        })
        .catch((err) => {
          if (requestId !== copyRequestId.current) return;
          console.error('buildCopySections failed', err);
          setCopyError('Failed to load services from source budget. Please try again or contact support.');
        })
        .finally(() => {
          // Only the latest run clears the spinner. An earlier one finishing late would otherwise
          // report the screen settled while the request whose answer is actually wanted is still out.
          if (requestId === copyRequestId.current) setIsReloadingCopy(false);
        });
    },
    [source_budget_id, client_id, price_plan_revision_id, package_fixed_id, start_date, end_date, ignore_sah_filters],
  );

  const handleCategoryMovesToggle = (next: boolean) => {
    // Ignored rather than queued while one is already running. The control has two positions and the
    // request in flight is already headed for one of them, so a press landing mid-flight is either a
    // repeat of what is coming or a reversal that the provider can make again the moment it lands -
    // and neither is worth a queue of reconciles against a budget they have not seen yet.
    if (isReloadingCopy) return;

    setApplyCategoryMoves(next);
    loadCopySections(next);
  };

  // PREFILL SECTIONS (on initial render if no sections exist yet)
  useEffect(() => {
    if (!price_plan_revision_id || !package_fixed_id) return;

    if ((value.sections?.length ?? 0) > 0) return;

    // Copy flow: fetch reconciled services from source budget
    if (source_budget_id && client_id) {
      loadCopySections(applyCategoryMoves);
      return;
    }

    // New budget flow: fetch prefilled services
    dispatch(
      buildSections({
        revision_id: price_plan_revision_id,
        client_id,
        package_fixed_id,
        start_date,
        end_date,
      }),
    )
      .then((res: any) => {
        if (Array.isArray(res) && res.length > 0) {
          onChange?.(name, res);
        }
      })
      .catch((err) => console.error('buildSections failed', err));
  }, [price_plan_revision_id, package_fixed_id, start_date, end_date]);

  // LOOKUP DATA LOADS
  useEffect(() => {
    dispatch(getServices({ deleted: [true, false] }));
    dispatch(getSahCategories());
    dispatch(getCalendarFrequencies());
    dispatch(getShifts());
  }, []);

  // FILTERED SERVICES QUERY
  const clientId = typeof client_id === 'string' ? parseInt(client_id, 10) : client_id;
  const pkgFixedId = typeof package_fixed_id === 'string' ? parseInt(package_fixed_id, 10) : package_fixed_id;

  const { data: filteredServices } = useGetFilteredServicesQuery(
    price_plan_revision_id
      ? {
          revision_id: price_plan_revision_id,
          client_id: clientId,
          package_fixed_id: pkgFixedId,
          ignore_sah_filters,
          // Sent so each entry comes back with the part of this budget's window its service covers.
          // Without it a service that ends part way through looks available for the whole budget.
          start_date,
          end_date,
        }
      : skipToken,
  );

  // CONTRIBUTIONS
  const {
    data: contributions,
    refetch: refetchContributions,
    isFetching: isRefreshingContributions,
  } = useGetContributionsQuery(
    client_id && package_fixed_id && start_date && end_date
      ? { client_id, package_fixed_id: pkgFixedId, start_date, end_date }
      : skipToken,
  );

  // RECALCULATE LOGIC
  const [recalculate] = useRecalculateMutation();

  const normalizeNumericValues = (item: any) => {
    const normalized = { ...item };
    if (typeof normalized.unit_amount === 'string') normalized.unit_amount = Number(normalized.unit_amount);
    if (typeof normalized.quantity === 'string') normalized.quantity = Number(normalized.quantity);
    return normalized;
  };

  /**
   * Ask the server what the figures should be, and take only the figures back.
   *
   * The merge target is deliberately `latestSections.current` and NOT the snapshot this run was
   * dispatched with. Merging into the snapshot is what made a slow recalc undo typing: everything
   * entered while the request was in flight was absent from the snapshot, so writing it back as the
   * whole sections array reverted those rows to the state they were in when the request left.
   *
   * Only the four fields the server actually computes are taken - the billing type it resolved and
   * the three money values. Everything else on a row is the provider's, and a response that
   * arrives after they have moved on must not be allowed to speak for it.
   */
  const runRecalc = async (sectionsSnapshot: Section[]) => {
    isRecalcRunning.current = true;
    // Read in `finally`, which cannot otherwise tell a run that merged its figures from one that
    // threw before it got there.
    let recalcFailed = true;
    try {
      const normalizedSections = sectionsSnapshot.map((section) => ({
        ...section,
        items: section.items.map((item) => normalizeNumericValues(item)),
      }));

      const result = await recalculate({
        sections: normalizedSections,
        price_plan_revision_id,
        start_date,
        end_date,
        total_amount,
        total_includes_case_management,
        supplements,
        client_sah_budget_id,
      }).unwrap();

      const serverSections = result.sections ?? result.data?.sections ?? [];
      const serverGrandTotal = result.grand_total ?? result.data?.grand_total ?? {};
      const serverSupplements = result.supplements ?? result.data?.supplements ?? [];
      const serverAvailableAmount = result.available_amount ?? result.data?.available_amount ?? 0;

      const mergedSections = latestSections.current.map((existingSection) => {
        const updatedSection = serverSections.find((sec: Section) => sec.category_id === existingSection.category_id);
        if (!updatedSection) return existingSection;

        const updatedItems = existingSection.items.map((item) => {
          const incoming = updatedSection.items.find((i: SectionItem) => i.id === item.id);
          if (!incoming) return item;
          return {
            ...item,
            billing_type: incoming.billing_type ?? item.billing_type,
            unit_amount: incoming.unit_amount ?? item.unit_amount,
            unit_amount_ph: incoming.unit_amount_ph ?? item.unit_amount_ph,
            total_amount: incoming.total_amount ?? 0,
            copy_status: item.copy_status,
            previous_unit_amount: item.previous_unit_amount,
          };
        });

        return {
          ...existingSection,
          items: updatedItems,
          totals: updatedSection.totals ?? existingSection.totals,
          service_totals: updatedSection.service_totals ?? existingSection.service_totals,
        };
      });

      onChange?.(name, mergedSections);
      onChange?.('grand_total', serverGrandTotal);
      onChange?.('supplements', serverSupplements);
      onChange?.('available_amount', serverAvailableAmount);
      setRecalcError(null);
      recalcFailed = false;
    } catch (err) {
      console.error('Recalculate failed:', err);
      // Nothing was merged - the merge is the last thing the try does - so every figure on screen
      // is still the one from before the edit that triggered this run. Clearing the spinners here
      // would present those figures as recalculated, which is the one thing this feature exists to
      // stop: a stale figure is indistinguishable from a correct one. The rows stay pending and
      // the banner says why, so the provider knows the totals are not to be trusted.
      setRecalcError(
        'The budget figures could not be recalculated. The amounts still waiting are the ones from before your last change.',
      );
    } finally {
      isRecalcRunning.current = false;
      if (pendingSectionsRef.current) {
        const next = pendingSectionsRef.current;
        pendingSectionsRef.current = null;
        runRecalc(next);
      } else if (!recalcFailed) {
        // Cleared only once nothing is queued behind this run. Clearing per-run instead would drop
        // the spinner off a row that changed again while its first recalc was still in flight, in
        // the window before the follow-up run puts it back.
        setPendingItemIds(new Set());
      }
    }
  };

  // Long enough that typing a two-digit quantity is one recalculation rather than two, short enough
  // that the figures land while the provider is still looking at the row they changed.
  const RECALC_DEBOUNCE_MS = 300;

  const debouncedRecalculate = (sectionsData: Section[]) => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    debounceTimer.current = setTimeout(() => {
      if (isRecalcRunning.current) {
        pendingSectionsRef.current = sectionsData;
        return;
      }
      runRecalc(sectionsData);
    }, RECALC_DEBOUNCE_MS);
  };

  // Kept in a ref rather than read from the closure, so a recalc that resolves later merges into
  // what is on screen now rather than into what was on screen when it was dispatched.
  latestSections.current = sections;

  useEffect(() => {
    if (!price_plan_revision_id) return;

    // Filter out new / incomplete items that shouldn't trigger recalculation
    const costRelevantSections = sections.map((section) => ({
      category_id: section.category_id,
      contrib_proportion: section.contrib_proportion,
      items: section.items
        .filter((item) => !!item.service_type_id)
        .map((item) => ({
          service_type_id: item.service_type_id,
          start_date: item.start_date,
          end_date: item.end_date,
          week_days: item.week_days,
          shift_id: item.shift_id,
          frequency_id: item.frequency_id,
          quantity: item.quantity,
          unit_amount: item.unit_amount,
          include_public_holidays: item.include_public_holidays,
        })),
    }));

    // Only include supplement properties that should TRIGGER recalc (add/remove)
    // Exclude computed outputs like amount, rate, timestamps
    // Sort by supplement_id to ensure consistent ordering
    const costRelevantSupplements = supplements
      .map((sup: any) => ({
        supplement_id: sup.supplement_id,
      }))
      .sort((a, b) => a.supplement_id.localeCompare(b.supplement_id));

    // Skip recalc when total_amount is missing — the API requires it and there's nothing to calculate
    if (!total_amount && total_amount !== 0) return;

    const costRelevantKey = JSON.stringify({
      price_plan_revision_id,
      sections: costRelevantSections,
      supplements: costRelevantSupplements,
      total_amount,
      total_includes_case_management,
      client_sah_budget_id,
    });

    // Skip recalc if nothing cost-relevant changed
    if (recalcKey.current === costRelevantKey) return;

    // Which rows actually moved. The same per-item fields the whole-budget key is built from, kept
    // per item so the spinner lands on the row the provider is editing rather than on all of them.
    const itemKeys = new Map<string, string>();
    for (const section of sections) {
      for (const item of section.items) {
        if (!item.service_type_id) continue;
        itemKeys.set(
          item.id,
          JSON.stringify([
            item.service_type_id,
            item.start_date,
            item.end_date,
            item.week_days,
            item.shift_id,
            item.frequency_id,
            item.quantity,
            item.unit_amount,
            item.include_public_holidays,
          ]),
        );
      }
    }

    const changedIds = [...itemKeys.entries()]
      .filter(([id, key]) => recalcItemKeys.current.get(id) !== key)
      .map(([id]) => id);

    // A first pass has nothing to compare against, so every row would read as changed - that is
    // the initial load, where the figures are already correct and no spinner is wanted.
    const isFirstPass = recalcItemKeys.current.size === 0;
    recalcItemKeys.current = itemKeys;

    if (!isFirstPass && changedIds.length > 0) {
      setPendingItemIds((prev) => new Set([...prev, ...changedIds]));
    }

    recalcKey.current = costRelevantKey;
    debouncedRecalculate(sections);
  }, [sections, price_plan_revision_id, supplements, total_amount, total_includes_case_management, client_sah_budget_id]);

  useEffect(() => {
    if (!contributions || !Array.isArray(contributions)) return;

    const updatedSections = sections.map((section) => {
      const contrib = contributions.find((con) => con.category_id === section.category_id);
      if (!contrib) return section;
      return {
        ...section,
        contrib_proportion: contrib.contrib_proportion ?? section.contrib_proportion,
      };
    });

    onChange?.(name, updatedSections);
  }, [contributions]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
        debounceTimer.current = null;
      }
    };
  }, []);

  const handleUpdate = (category_id: string, items: SectionItem[]) => {
    const sectionIndex = sections.findIndex((section) => section.category_id === category_id);
    if (sectionIndex === -1) return;

    const updatedSections = [...sections];
    updatedSections[sectionIndex] = {
      ...updatedSections[sectionIndex],
      items,
    };

    onChange?.(name, updatedSections);
  };

  // Delete handler & confirmation modal callback

  const confirmDelete = (confirmed: boolean) => {
    if (confirmed && pendingDelete) {
      const { id, category_id } = pendingDelete;

      const updatedSections = sections.map((section) =>
        section.category_id === category_id
          ? { ...section, items: section.items.filter((item) => item.id !== id) }
          : section,
      );

      onChange?.(name, updatedSections);
    }
    setPendingDelete(null);
  };

  const handleDelete = (sectionId: string, item: SectionItem) => {
    setPendingDelete({ id: item.id, category_id: sectionId });
  };

  /**
   * Bring a saved budget back into line with the setup as it stands now.
   *
   * Two things go stale on an existing budget: the contribution rate captured when it was built,
   * and - since a Support at Home service can now end part way through a budget period - the dates
   * on lines whose service is no longer available for the whole of their range.
   *
   * Both are refreshed here, and only ever on the provider's say-so. Nothing is refreshed on load
   * or on save: a budget's saved figures are what they are until someone asks for them to be
   * brought up to date. No line is added and none is removed - a service that has started part way
   * through the period is not populated, because adding a service to a saved budget is a decision,
   * not a correction.
   */
  const handleRefreshBudgetValues = async () => {
    if (!client_id || !package_fixed_id || !start_date || !end_date) return;

    try {
      const { data: refreshed } = await refetchContributions();

      const withContributions =
        refreshed && Array.isArray(refreshed)
          ? sections.map((section) => {
              const contrib = refreshed.find((c) => c.category_id === section.category_id);
              if (!contrib) return section;
              return {
                ...section,
                contrib_proportion: contrib.contrib_proportion ?? section.contrib_proportion,
              };
            })
          : sections;

      onChange?.(name, refreshItemDatesToServicePeriods(withContributions, filteredServices ?? []));
    } catch (err) {
      console.error('Failed to refresh budget values', err);
    }
  };

  return (
    <div>
      <ConfirmDialog
        isOpen={!!pendingDelete}
        messages={
          <p>
            <strong>Warning:</strong> This record will be deleted.
          </p>
        }
        onClose={confirmDelete}
        useList={false}
      />
      <BudgetSupplementsModal
        isOpen={isSupplementsOpen}
        setIsOpen={setIsSupplementsOpen}
        value={{ supplements, package_fixed_id }}
        onChange={onChange}
      />
      <BudgetDetailsModal isOpen={isDetailsOpen} setIsOpen={setIsDetailsOpen} values={value} onChange={onChange} />
      {/* Outer Container for all sections */}
      <div className="budget-container">
        <div className="budget-sections">
          {/* Allocated is the lines only, as the budget list and the Budget Tracker read it (TAV-3343). The Grand Total
              row below adds the care management fee and care management supplements on top. */}
          <BudgetTotalIndicator
            allocated={sections.length > 0 ? allocatedOfSections(sections) : total_allocated}
            available={available_amount}
            client_id={client_id}
          />
          <Toolbar
            id="budget-toolbar"
            leftCluster={
              <>
                <div className="d-flex justify-content-end align-items-center ms-4">
                  <Label className="me-3 fw-bold" for="ignore-sah-filters">
                    Ignore SAH Filters:
                  </Label>
                  <Switch
                    type="switch"
                    id="ignore-sah-filters"
                    name="ignore-sah-filters"
                    value={ignore_sah_filters}
                    onChange={() => onChange?.('ignore_sah_filters', !ignore_sah_filters)}
                  />
                </div>
              </>
            }
            rightCluster={
              !isNew ? (
                <>
                  <Button
                    id="supplements-button"
                    color={supplementsStale ? 'warning' : undefined}
                    onClick={() => setIsSupplementsOpen(true)}
                    size="sm"
                  >
                    Edit Supplements
                  </Button>
                  {supplementsStale && (
                    <TooltipPortal targetId="supplements-button" content="Supplement rates need to be updated" />
                  )}
                  <Button id="edit-button" onClick={() => setIsDetailsOpen(true)} size="sm" className="ms-2">
                    Edit Budget Details
                  </Button>
                  <ClickableIcon
                    id="refresh-values-btn"
                    toolTip="Refresh co-contributions and service dates"
                    icon={faRefresh}
                    onClick={handleRefreshBudgetValues}
                    disabled={isRefreshingContributions}
                  />
                </>
              ) : undefined
            }
          />

          {copyError && <ErrorResponse message={copyError} />}
          {recalcError && <ErrorResponse message={recalcError} />}
          {/*
            Above the sections rather than on the row: a provider scanning a copied budget for what
            changed should not have to hover every line to find out that one moved category, and the
            moved lines are spread across two sections by definition - the one they left and the one
            they arrived in.
          */}
          <CategoryMoveNotice
            groups={groupMovesFromSections(sections, (serviceTypeId) =>
              (filteredServices ?? []).find(({ id }: { id: string }) => id === serviceTypeId)?.name,
            )}
            applyCategoryMoves={applyCategoryMoves}
            onToggle={handleCategoryMovesToggle}
            disabled={price_plan_deleted || isReloadingCopy}
            isLoading={isReloadingCopy}
          />
          {sections.map((section: Section) => {
            const initialValues = initialBudget?.sections?.find(
              ({ category_id }) => category_id === section.category_id,
            );
            return (
              <BudgetSection
                key={section.category_id}
                id={section.category_id}
                section={section}
                initialValues={initialValues}
                start_date={start_date}
                end_date={end_date}
                readonly={price_plan_deleted}
                filteredServices={filteredServices ?? []}
                pendingItemIds={pendingItemIds}
                onUpdate={handleUpdate}
                onDelete={handleDelete}
              />
            );
          })}
        </div>
        <div className="totals-container">
          <BudgetSupplementTotal id="supplements-grand-total" supplements={supplements} />
          <BudgetGrandTotal
            id="sections-grand-total"
            pending={pendingItemIds.size > 0}
            showCaseManagement={showCaseManagement({ package_fixed_id })}
            totals={grand_total}
            amount={total_amount}
            supplements={supplements}
            includesCaseManagement={total_includes_case_management}
          />
        </div>
      </div>
    </div>
  );
};

export default BudgetSections;
